import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faChevronLeft,
  faChevronRight,
  faSpinner,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import './TableControlBar.css';

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100];

const clampPageIndex = (pageIndex, pageCount) => {
  if (!pageCount) {
    return 0;
  }

  return Math.min(Math.max(pageIndex || 0, 0), pageCount - 1);
};

const getVisiblePageIndexes = (pageIndex, pageCount) => {
  if (pageCount <= 5) {
    return Array.from(Array(pageCount).keys());
  }

  const safePageIndex = clampPageIndex(pageIndex, pageCount);
  const start = Math.min(Math.max(safePageIndex - 2, 0), pageCount - 5);

  return Array.from(Array(5).keys()).map(index => start + index);
};

const TableControlBar = ({
  actions,
  canNextPage,
  canPreviousPage,
  className,
  filters,
  gotoPage,
  isRefreshing,
  nextPage,
  onRefresh,
  pageCount,
  pageIndex,
  pageSize,
  previousPage,
  rowCount,
  rowsPerPageOptions,
  search,
  setPageSize,
  visibleRowCount
}) => {
  const safePageCount = Math.max(pageCount || 0, rowCount > 0 ? 1 : 0);
  const safePageIndex = clampPageIndex(pageIndex, safePageCount);
  const safePageSize = pageSize || rowsPerPageOptions[0];
  const currentVisibleRowCount =
    typeof visibleRowCount === 'number'
      ? visibleRowCount
      : Math.min(safePageSize, rowCount);
  const rangeStart = rowCount > 0 ? safePageIndex * safePageSize + 1 : 0;
  const rangeEnd =
    rowCount > 0
      ? Math.min(rangeStart + currentVisibleRowCount - 1, rowCount)
      : 0;
  const visiblePageIndexes = getVisiblePageIndexes(
    safePageIndex,
    safePageCount
  );
  const [pageInputValue, setPageInputValue] = useState(`${safePageIndex + 1}`);

  useEffect(() => {
    setPageInputValue(`${safePageIndex + 1}`);
  }, [safePageIndex, safePageCount]);

  const goToSafePage = nextPageIndex => {
    if (gotoPage && safePageCount > 0) {
      gotoPage(clampPageIndex(nextPageIndex, safePageCount));
    }
  };

  const submitPageInput = () => {
    const requestedPage = parseInt(pageInputValue, 10);

    if (Number.isNaN(requestedPage)) {
      setPageInputValue(`${safePageIndex + 1}`);
      return;
    }

    const safeRequestedPageIndex = clampPageIndex(
      requestedPage - 1,
      safePageCount
    );

    setPageInputValue(`${safeRequestedPageIndex + 1}`);
    goToSafePage(safeRequestedPageIndex);
  };

  const hasUtilityControls = filters || search || actions;

  return (
    <div
      className={classNames(
        'table-control-bar px-3 py-2 border-bottom bg-light',
        className
      )}
    >
      {hasUtilityControls && (
        <div
          data-testid="table-control-utility-row"
          className="table-control-bar__utility-row"
        >
          <div className="table-control-bar__utility-main">
            {filters && (
              <div className="table-control-bar__filters">{filters}</div>
            )}

            {search && (
              <div className="table-control-bar__search">{search}</div>
            )}
          </div>

          {actions && (
            <div className="table-control-bar__actions">{actions}</div>
          )}
        </div>
      )}

      <div
        data-testid="table-control-pagination-row"
        className={classNames('table-control-bar__pagination-row', {
          'table-control-bar__pagination-row--separated': hasUtilityControls
        })}
      >
        {onRefresh && (
          <Button
            aria-label="Refresh"
            title="Refresh"
            size="sm"
            variant="falcon-default"
            onClick={onRefresh}
            disabled={isRefreshing}
            className="table-control-bar__icon-button px-2"
          >
            <FontAwesomeIcon
              icon={isRefreshing ? faSpinner : faSyncAlt}
              spin={isRefreshing}
            />
          </Button>
        )}

        <span className="table-control-bar__range fs--1 text-700">
          {rangeStart}-{rangeEnd} of {rowCount} rows
        </span>

        <div className="table-control-bar__control-group table-control-bar__rows-group">
          <span className="table-control-bar__label fs--1 text-700">
            Rows per page
          </span>
          <Form.Select
            aria-label="Rows per page"
            className="table-control-bar__select"
            size="sm"
            value={safePageSize}
            onChange={event => {
              if (setPageSize) {
                setPageSize(Number(event.target.value));
              }
            }}
          >
            {rowsPerPageOptions.map(value => (
              <option value={value} key={value}>
                {value}
              </option>
            ))}
          </Form.Select>
        </div>

        <div className="table-control-bar__page-buttons">
          <Button
            aria-label="First page"
            title="First page"
            size="sm"
            variant="falcon-default"
            className={classNames('px-2', { disabled: !canPreviousPage })}
            onClick={() => goToSafePage(0)}
            disabled={!canPreviousPage}
          >
            <FontAwesomeIcon icon={faAngleDoubleLeft} />
          </Button>
          <Button
            aria-label="Previous page"
            title="Previous page"
            size="sm"
            variant="falcon-default"
            className={classNames('px-2', { disabled: !canPreviousPage })}
            onClick={previousPage}
            disabled={!canPreviousPage}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </Button>

          {visiblePageIndexes.map(currentPageIndex => (
            <Button
              key={currentPageIndex}
              aria-label={`Page ${currentPageIndex + 1}`}
              size="sm"
              variant={
                safePageIndex === currentPageIndex
                  ? 'primary'
                  : 'falcon-default'
              }
              className="px-2"
              onClick={() => goToSafePage(currentPageIndex)}
            >
              {currentPageIndex + 1}
            </Button>
          ))}

          <Button
            aria-label="Next page"
            title="Next page"
            size="sm"
            variant="falcon-default"
            className={classNames('px-2', { disabled: !canNextPage })}
            onClick={nextPage}
            disabled={!canNextPage}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </Button>
          <Button
            aria-label="Last page"
            title="Last page"
            size="sm"
            variant="falcon-default"
            className={classNames('px-2', { disabled: !canNextPage })}
            onClick={() => goToSafePage(safePageCount - 1)}
            disabled={!canNextPage}
          >
            <FontAwesomeIcon icon={faAngleDoubleRight} />
          </Button>
        </div>

        <div className="table-control-bar__control-group table-control-bar__page-jump">
          <span className="table-control-bar__label fs--1 text-700">Page</span>
          <Form.Control
            aria-label="Page number"
            className="table-control-bar__page-input"
            size="sm"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pageInputValue}
            onChange={event => setPageInputValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitPageInput();
              }
            }}
            disabled={safePageCount < 2}
          />
          <span className="table-control-bar__label fs--1 text-700">
            of {safePageCount || 1}
          </span>
        </div>
      </div>
    </div>
  );
};

TableControlBar.propTypes = {
  actions: PropTypes.node,
  canNextPage: PropTypes.bool,
  canPreviousPage: PropTypes.bool,
  className: PropTypes.string,
  filters: PropTypes.node,
  gotoPage: PropTypes.func,
  isRefreshing: PropTypes.bool,
  nextPage: PropTypes.func,
  onRefresh: PropTypes.func,
  pageCount: PropTypes.number,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  previousPage: PropTypes.func,
  rowCount: PropTypes.number,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  search: PropTypes.node,
  setPageSize: PropTypes.func,
  visibleRowCount: PropTypes.number
};

TableControlBar.defaultProps = {
  canNextPage: false,
  canPreviousPage: false,
  isRefreshing: false,
  nextPage: () => {},
  pageCount: 0,
  pageIndex: 0,
  pageSize: 10,
  previousPage: () => {},
  rowCount: 0,
  rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS
};

export default TableControlBar;
